// src/components/Subscription/UpgradeModal.jsx
//
// The single global upgrade modal. Reads modalProps from useUpgradeModal()
// and shows a comparison of the user's current plan vs. the required tier,
// with monthly/yearly billing toggle (matches PricingPlans.js style) and
// CTAs for free trial + plan comparison.
//
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useUpgradeModal } from '../../contexts/UpgradeModalContext';

// ── Plan pricing (mirrors PricingPlans.js — keep in sync) ────────────────────
const PLAN_PRICING = {
  standard: { monthly: 2.99, yearly: 28.70, color: '#3b82f6' },
  premium:  { monthly: 4.99, yearly: 47.90, color: '#8b5cf6' },
  family:   { monthly: 9.99, yearly: 95.90, color: '#10b981' },
  business: { monthly: 24.99, yearly: 239.90, color: '#f59e0b' },
};

const planSavingsPct = (plan) => {
  const p = PLAN_PRICING[plan];
  if (!p || !p.monthly) return 0;
  return Math.round(100 * (1 - p.yearly / (p.monthly * 12)));
};

// Bullets per tier — 3-4 short benefits to surface in the modal.
const PLAN_BENEFITS = {
  standard: [
    'subscription.upgrade.benefits.standard.0',
    'subscription.upgrade.benefits.standard.1',
    'subscription.upgrade.benefits.standard.2',
    'subscription.upgrade.benefits.standard.3',
  ],
  premium: [
    'subscription.upgrade.benefits.premium.0',
    'subscription.upgrade.benefits.premium.1',
    'subscription.upgrade.benefits.premium.2',
    'subscription.upgrade.benefits.premium.3',
  ],
  family: [
    'subscription.upgrade.benefits.family.0',
    'subscription.upgrade.benefits.family.1',
    'subscription.upgrade.benefits.family.2',
    'subscription.upgrade.benefits.family.3',
  ],
  business: [
    'subscription.upgrade.benefits.business.0',
    'subscription.upgrade.benefits.business.1',
    'subscription.upgrade.benefits.business.2',
    'subscription.upgrade.benefits.business.3',
  ],
};

// English fallbacks for the benefit bullets so the modal still works even
// if i18n keys are missing in some locale.
const BENEFIT_FALLBACKS = {
  standard: [
    '200 transactions / month',
    '15 budget categories',
    'CSV data export',
    '10 split bills / month',
  ],
  premium: [
    'Unlimited transactions & categories',
    'AI-powered spending insights',
    'Advanced analytics & charts',
    'Priority support',
  ],
  family: [
    'Everything in Premium',
    'Up to 5 family members',
    'Shared budgets & categories',
    'Household analytics',
  ],
  business: [
    'Everything in Family',
    'API access',
    'Business reporting',
    'Dedicated support',
  ],
};

const UpgradeModal = () => {
  const { isOpen, close, modalProps } = useUpgradeModal();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const requiredPlan = modalProps?.requiredPlan || 'premium';
  const currentPlan = modalProps?.currentPlan || 'free';
  const promoText = modalProps?.promoText || '';

  const pricing = PLAN_PRICING[requiredPlan] || PLAN_PRICING.premium;
  const savings = useMemo(() => planSavingsPct(requiredPlan), [requiredPlan]);
  const displayPrice =
    billingCycle === 'yearly'
      ? (pricing.yearly / 12).toFixed(2)
      : pricing.monthly.toFixed(2);

  const benefits = PLAN_BENEFITS[requiredPlan] || PLAN_BENEFITS.premium;
  const fallbacks = BENEFIT_FALLBACKS[requiredPlan] || BENEFIT_FALLBACKS.premium;

  const planLabel = t(`subscription.locked.${requiredPlan}`, requiredPlan.toUpperCase());
  const currentPlanLabel = t(`subscription.locked.${currentPlan}`, currentPlan.toUpperCase());

  const handleStartTrial = () => {
    close();
    navigate(
      `/dashboard/subscription/checkout?plan=${requiredPlan}&trial=true&cycle=${billingCycle}`
    );
  };

  const handleComparePlans = () => {
    close();
    navigate('/dashboard/subscription/plans');
  };

  return (
    <Dialog
      open={!!isOpen}
      onClose={close}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* ── Hero header strip ──────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${pricing.color} 0%, ${theme.palette.primary.main} 100%)`,
          color: '#fff',
          px: 3,
          py: 2.5,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={close}
          aria-label="close"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#fff',
            '&:hover': { background: 'rgba(255,255,255,0.15)' },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RocketLaunchIcon sx={{ color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.85, lineHeight: 1, letterSpacing: 1 }}>
              {planLabel}
            </Typography>
            <DialogTitle
              component="div"
              sx={{ p: 0, color: '#fff', fontSize: '1.25rem', fontWeight: 800 }}
            >
              {t('subscription.upgrade.title', 'Unlock {{feature}}', {
                feature: promoText || planLabel,
              })}
            </DialogTitle>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* ── Current vs required plan ────────────────────────────── */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('subscription.upgrade.currentPlan', 'Your current plan')}
            </Typography>
            <Chip
              label={currentPlanLabel}
              size="small"
              variant="outlined"
              sx={{ mt: 0.5, fontWeight: 700, display: 'flex', width: 'fit-content' }}
            />
          </Box>
          <Box sx={{ color: 'text.secondary', fontSize: '1.5rem' }}>→</Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('subscription.upgrade.requiredPlan', 'Required plan')}
            </Typography>
            <Chip
              icon={<LockIcon sx={{ fontSize: 14 }} />}
              label={planLabel}
              size="small"
              sx={{
                mt: 0.5,
                fontWeight: 800,
                color: '#fff',
                background: pricing.color,
                width: 'fit-content',
                display: 'flex',
              }}
            />
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* ── Benefit bullets ─────────────────────────────────────── */}
        <List dense sx={{ py: 0 }}>
          {benefits.map((key, idx) => (
            <ListItem key={key} sx={{ px: 0, py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckIcon sx={{ fontSize: 18, color: pricing.color }} />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ fontSize: '0.92rem', fontWeight: 500 }}
                primary={t(key, fallbacks[idx])}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* ── Billing cycle toggle (mirrors PricingPlans.js) ──────── */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 1.5 }}>
          <Typography
            onClick={() => setBillingCycle('monthly')}
            sx={{
              fontWeight: billingCycle === 'monthly' ? 700 : 500,
              color: billingCycle === 'monthly' ? 'text.primary' : 'text.secondary',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '0.9rem',
            }}
          >
            {t('subscription.upgrade.monthly', 'Monthly')}
          </Typography>
          <Box
            onClick={() => setBillingCycle((c) => (c === 'monthly' ? 'yearly' : 'monthly'))}
            sx={{
              width: 48,
              height: 26,
              borderRadius: 13,
              cursor: 'pointer',
              background:
                billingCycle === 'yearly'
                  ? `linear-gradient(135deg, ${pricing.color}, ${theme.palette.primary.main})`
                  : theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.15)'
                  : 'rgba(0,0,0,0.12)',
              position: 'relative',
              transition: 'background 0.3s',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                left: billingCycle === 'yearly' ? 24 : 2,
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: '#fff',
                transition: 'left 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
              }}
            />
          </Box>
          <Typography
            onClick={() => setBillingCycle('yearly')}
            sx={{
              fontWeight: billingCycle === 'yearly' ? 700 : 500,
              color: billingCycle === 'yearly' ? 'text.primary' : 'text.secondary',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '0.9rem',
            }}
          >
            {t('subscription.upgrade.yearly', 'Yearly (save {{percent}}%)', {
              percent: savings,
            })}
          </Typography>
        </Stack>

        {/* ── Price display ───────────────────────────────────────── */}
        <Box
          sx={{
            textAlign: 'center',
            py: 1.5,
            borderRadius: 2,
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, color: pricing.color, lineHeight: 1 }}>
            €{displayPrice}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5, fontWeight: 500 }}>
              /mo
            </Typography>
          </Typography>
          {billingCycle === 'yearly' && (
            <Typography variant="caption" color="text.secondary">
              €{pricing.yearly.toFixed(2)}/year
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleStartTrial}
          sx={{
            background: `linear-gradient(135deg, ${pricing.color} 0%, ${theme.palette.primary.main} 100%)`,
            color: '#fff',
            fontWeight: 700,
            textTransform: 'none',
            py: 1.25,
            borderRadius: 2,
            '&:hover': {
              filter: 'brightness(1.05)',
              background: `linear-gradient(135deg, ${pricing.color} 0%, ${theme.palette.primary.main} 100%)`,
            },
          }}
        >
          {t('subscription.upgrade.tryFree', 'Try {{plan}} free for 7 days', {
            plan: planLabel,
          })}
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={handleComparePlans}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {t('subscription.upgrade.comparePlans', 'Compare all plans')}
        </Button>
        <Button
          fullWidth
          variant="text"
          color="inherit"
          onClick={close}
          sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.85rem' }}
        >
          {t('subscription.upgrade.maybeLater', 'Maybe later')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeModal;
